import { Grid, Stack, Typography } from '@mui/material';
import { includes, isEmpty } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { assetsApi } from 'api';
import {
  DataWrapper,
  InfoCard,
  InternalUserWallet,
  MyAssets,
  PageHeader,
  TotalBalance,
} from 'components';
import { QueryKey, UserRole } from 'enums';
import {
  useCalculateAssetBalances,
  useCurrencies,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';

export const MerchantMainPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Merchant, {
    keyPrefix: 'pages.assets.balances',
  });

  const queryResult = useQuery(QueryKey.ShopAssets, assetsApi.getAllShops);
  const { balances } = useCalculateAssetBalances(queryResult.data);

  const { merchant } = useUserContext();
  const { defaultAssetCurrency, hiddenAssetCurrencyIds } = useCurrencies();

  const allBalances = useMemo(
    () => [
      ...(merchant?.depositAmount &&
      defaultAssetCurrency?.id &&
      !includes(hiddenAssetCurrencyIds, defaultAssetCurrency.id)
        ? [
            {
              id: defaultAssetCurrency.id,
              amount: merchant.depositAmount,
              title: t('deposit_amount'),
            },
          ]
        : []),
      ...balances,
    ],
    [
      balances,
      defaultAssetCurrency?.id,
      hiddenAssetCurrencyIds,
      merchant?.depositAmount,
      t,
    ],
  );

  return (
    <Fragment>
      <PageHeader title={t('title')} />
      <Stack direction="column" spacing={8}>
        <DataWrapper
          isLoading={queryResult.isLoading}
          isEmpty={isEmpty(allBalances)}
          isError={queryResult.isError}
        >
          <TotalBalance balances={allBalances} />
        </DataWrapper>
        {merchant?.customBalanceInfo?.enabled && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <InfoCard
                title={merchant.customBalanceInfo.header}
                content={
                  merchant.customBalanceInfo.content ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {merchant.customBalanceInfo.content}
                    </Typography>
                  ) : null
                }
              />
            </Grid>
          </Grid>
        )}
        <PageHeader title={t('my_shops')} divider={false} />
        <MyAssets role={UserRole.Merchant} />
        <PageHeader title={t('my_wallets')} divider={false} />
        <InternalUserWallet />
      </Stack>
    </Fragment>
  );
};
